import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, CheckCircle, CreditCard, FileSignature, Mail, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, Button } from '../components';
import api, { authAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';

type WizardStep = 'verify' | 'kvk' | 'contract' | 'profile' | 'stripe';

const STEP_ORDER: WizardStep[] = ['verify', 'kvk', 'contract', 'profile', 'stripe'];

function deriveStep(isVerified: boolean, status: string | null | undefined): WizardStep | 'done' {
  if (!isVerified) return 'verify';
  switch (status) {
    case 'kvk_verified':
      return 'contract';
    case 'contract_signed':
      return 'profile';
    case 'profile_complete':
      return 'stripe';
    case 'company_active':
      return 'done';
    default:
      // registration / email_verified / onbekend
      return 'kvk';
  }
}

export const EmployerOnboardingPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const navigate = useNavigate();
  const { user, updateUser } = useAuthStore();

  const [profile, setProfile] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [busy, setBusy] = useState(false);

  // KvK-stap
  const [kvkNummer, setKvkNummer] = useState('');
  const [kvkData, setKvkData] = useState<any>(null);
  const [kvkLoading, setKvkLoading] = useState(false);

  // Contract-stap (SMS-2FA, artikel 15)
  const [contractText, setContractText] = useState('');
  const [telefoon, setTelefoon] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showSignedModal, setShowSignedModal] = useState(false);

  // Profiel-stap
  const [beschrijving, setBeschrijving] = useState('');
  const [arboBeleid, setArboBeleid] = useState(false);
  const [contactpersoon, setContactpersoon] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const isVerified = !!user?.is_verified;
  const step = deriveStep(isVerified, profile?.onboarding_status);

  const loadProfile = useCallback(async () => {
    try {
      const response = await api.get('/employer/profile');
      if (response.data.success) {
        const p = response.data.data;
        setProfile(p);
        if (p) {
          setKvkNummer(p.kvk_nummer || '');
          setBeschrijving(p.beschrijving || '');
          setContactpersoon(p.contactpersoon || '');
        }
      }
    } catch (error) {
      console.error('Error loading employer profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'opdrachtgever') loadProfile();
  }, [user?.role, loadProfile]);

  useEffect(() => {
    if (step === 'contract' && !contractText) {
      api.get('/employer/onboarding/contract')
        .then((res) => setContractText(res.data.data?.contract_text || ''))
        .catch((err) => console.error('Error loading contract text:', err));
    }
  }, [step, contractText]);

  useEffect(() => {
    if (step === 'done') navigate('/employer/dashboard', { replace: true });
  }, [step, navigate]);

  if (!user || user.role !== 'opdrachtgever') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card>
          <p className="p-6 text-sm text-gray-700">
            {t('onboarding.employerOnly', 'De onboarding is alleen beschikbaar voor de hoofdgebruiker van een opdrachtgever-account.')}
          </p>
        </Card>
      </div>
    );
  }

  const refreshVerification = async () => {
    setBusy(true);
    try {
      const res = await authAPI.me();
      const payload = res.data.data as any;
      const freshUser = payload?.user ?? payload;
      if (freshUser) updateUser(freshUser);
      if (freshUser?.is_verified) {
        await loadProfile();
        toast.success(t('onboarding.verifiedToast', 'E-mailadres geverifieerd, u kunt verder.'));
      } else {
        toast(t('onboarding.notVerifiedYet', 'Nog niet geverifieerd. Klik op de link in uw e-mail.'));
      }
    } catch {
      toast.error(t('onboarding.refreshError', 'Kon status niet verversen.'));
    } finally {
      setBusy(false);
    }
  };

  const resendVerification = async () => {
    setBusy(true);
    try {
      await authAPI.resendVerification(user.email);
      toast.success(t('onboarding.resendSent', 'Nieuwe verificatielink verstuurd. Controleer uw inbox.'));
    } catch {
      toast.error(t('onboarding.resendError', 'Versturen mislukt. Probeer het later opnieuw.'));
    } finally {
      setBusy(false);
    }
  };

  const lookupKvk = async () => {
    if (!/^\d{8}$/.test(kvkNummer)) {
      toast.error(t('onboarding.kvkInvalid', 'Voer een geldig KvK-nummer van 8 cijfers in.'));
      return;
    }
    setKvkLoading(true);
    setKvkData(null);
    try {
      const response = await api.get('/employer/kvk-data', { params: { kvkNummer } });
      if (response.data.success && response.data.data) {
        setKvkData(response.data.data);
      } else {
        toast.error(response.data.message || t('onboarding.kvkNotFound', 'Geen bedrijfsgegevens gevonden voor dit KvK-nummer.'));
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('onboarding.kvkError', 'Kon bedrijfsgegevens niet ophalen bij de KvK.'));
    } finally {
      setKvkLoading(false);
    }
  };

  const confirmKvk = async () => {
    if (!kvkData) return;
    setBusy(true);
    try {
      const response = await api.post('/employer/onboarding/kvk-confirm', {
        kvk_nummer: kvkNummer,
        bedrijfsnaam: kvkData.bedrijfsnaam,
        adres: kvkData.adres || null,
        postcode: kvkData.postcode || null,
        plaats: kvkData.plaats || null,
        website: kvkData.website || null,
      });
      if (response.data.success) {
        setProfile(response.data.data);
        toast.success(t('onboarding.kvkConfirmed', 'Bedrijfsgegevens vastgelegd.'));
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('onboarding.stepError', 'Er ging iets mis. Probeer het opnieuw.'));
    } finally {
      setBusy(false);
    }
  };

  const requestSmsCode = async () => {
    if (!/^(\+31|0031|0)\s?6[\s-]?(\d[\s-]?){8}$/.test(telefoon.trim())) {
      toast.error(t('onboarding.phoneInvalid', 'Voer een geldig Nederlands mobiel nummer in (06...).'));
      return;
    }
    setSmsSending(true);
    try {
      const response = await api.post('/employer/onboarding/contract/request-code', { telefoon: telefoon.trim() });
      if (response.data.success) {
        setSmsSent(true);
        toast.success(t('onboarding.smsSent', 'SMS-code verstuurd naar {{telefoon}}.', { telefoon: response.data.data?.telefoon || telefoon }));
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('onboarding.smsError', 'De SMS-code kon niet worden verstuurd.'));
    } finally {
      setSmsSending(false);
    }
  };

  const signContract = async () => {
    if (!/^\d{6}$/.test(smsCode.trim()) || !agreedToTerms) {
      toast.error(t('onboarding.signAndAgreeRequired', 'Voer de 6-cijferige SMS-code in en ga akkoord met de voorwaarden.'));
      return;
    }
    setBusy(true);
    try {
      const response = await api.post('/employer/contract/sign', {
        sms_code: smsCode.trim(),
        agrees_to_terms: agreedToTerms,
      });
      if (response.data.success) {
        setShowSignedModal(true);
        await loadProfile();
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        // Al eerder getekend: door naar de volgende stap
        await loadProfile();
      } else {
        toast.error(error?.response?.data?.message || t('onboarding.signError', 'Ondertekenen mislukt. Probeer het opnieuw.'));
      }
    } finally {
      setBusy(false);
    }
  };

  const saveProfileStep = async () => {
    setBusy(true);
    try {
      if (logoFile) {
        const formData = new FormData();
        formData.append('company_photo', logoFile);
        await api.post('/employer/profile/photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      // Bestaande profielwaarden meesturen zodat PUT ze niet leegmaakt
      await api.put('/employer/profile', {
        bedrijfsnaam: profile?.bedrijfsnaam,
        kvk_nummer: profile?.kvk_nummer || '',
        website: profile?.website || '',
        adres: profile?.adres || '',
        postcode: profile?.postcode || '',
        plaats: profile?.plaats || '',
        factuur_email: profile?.factuur_email || '',
        aantal_medewerkers: profile?.aantal_medewerkers || '',
        bedrijfsgrootte: profile?.bedrijfsgrootte || '',
        contactpersoon_functie: profile?.contactpersoon_functie || '',
        beschrijving,
        contactpersoon,
      });
      if (arboBeleid) await api.post('/employer/arbo-beleid', { verklaard: true });
      const response = await api.post('/employer/onboarding/profile-complete');
      if (response.data.success) {
        setProfile(response.data.data);
        toast.success(t('onboarding.profileSaved', 'Bedrijfsprofiel afgerond.'));
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('onboarding.stepError', 'Er ging iets mis. Probeer het opnieuw.'));
    } finally {
      setBusy(false);
    }
  };

  const activateCockpit = async () => {
    setBusy(true);
    try {
      const response = await api.post('/employer/onboarding/activate');
      if (response.data.success) {
        toast.success(t('onboarding.activated', 'Uw HR-Cockpit is vrijgegeven!'));
        navigate('/employer/dashboard', { replace: true });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('onboarding.stepError', 'Er ging iets mis. Probeer het opnieuw.'));
    } finally {
      setBusy(false);
    }
  };

  const stepIndex = step === 'done' ? STEP_ORDER.length : STEP_ORDER.indexOf(step);
  const stepMeta: Array<{ key: WizardStep; label: string; icon: React.ReactNode }> = [
    { key: 'verify', label: t('onboarding.stepVerify', 'E-mail'), icon: <Mail className="w-4 h-4" /> },
    { key: 'kvk', label: t('onboarding.stepKvk', 'KvK-verificatie'), icon: <Building2 className="w-4 h-4" /> },
    { key: 'contract', label: t('onboarding.stepContract', 'Platformovereenkomst'), icon: <FileSignature className="w-4 h-4" /> },
    { key: 'profile', label: t('onboarding.stepProfile', 'Bedrijfsprofiel'), icon: <ShieldCheck className="w-4 h-4" /> },
    { key: 'stripe', label: t('onboarding.stepPayment', 'Betaalkoppeling'), icon: <CreditCard className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            {t('onboarding.title', 'Onboarding: naar uw HR-Cockpit')}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('onboarding.subtitle', 'Niet te veel. Niet te weinig. Precies goed.')}
          </p>
        </div>

        {/* Stappenbalk */}
        <div className="flex items-center justify-center gap-2 sm:gap-4 mb-8 flex-wrap">
          {stepMeta.map((s, i) => {
            const done = i < stepIndex;
            const active = i === stepIndex;
            return (
              <div key={s.key} className="flex items-center gap-1.5">
                <div
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
                    done
                      ? 'bg-green-50 border-green-200 text-green-700'
                      : active
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-200 text-gray-400'
                  }`}
                >
                  {done ? <CheckCircle className="w-4 h-4" /> : s.icon}
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
                {i < stepMeta.length - 1 && <div className="w-4 h-px bg-gray-300 hidden sm:block" />}
              </div>
            );
          })}
        </div>

        {loadingProfile ? (
          <Card>
            <div className="p-10 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
            </div>
          </Card>
        ) : (
          <>
            {step === 'verify' && (
              <Card>
                <div className="p-8 text-center">
                  <Mail className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('onboarding.verifyTitle', 'Bevestig eerst uw e-mailadres')}
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">
                    {t('onboarding.verifyText', 'We hebben een activatielink gestuurd naar {{email}}. Klik op de knop in die e-mail en kom hier terug.', { email: user.email })}
                  </p>
                  <div className="flex gap-3 justify-center">
                    <Button onClick={refreshVerification} loading={busy}>
                      {t('onboarding.verifyDone', 'Ik heb geverifieerd')}
                    </Button>
                    <Button variant="outline" onClick={resendVerification} disabled={busy}>
                      {t('onboarding.resend', 'Nieuwe link versturen')}
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {step === 'kvk' && (
              <Card>
                <div className="p-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('onboarding.kvkTitle', 'Stap 1: KvK-verificatie')}
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">
                    {t('onboarding.kvkText', 'Vul uw KvK-nummer in; wij laden uw officiële bedrijfsgegevens direct via de KvK API.')}
                  </p>
                  <div className="flex gap-3 mb-4">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={8}
                      value={kvkNummer}
                      onChange={(e) => setKvkNummer(e.target.value.replace(/\D/g, ''))}
                      placeholder="12345678"
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    <Button onClick={lookupKvk} loading={kvkLoading}>
                      {t('onboarding.kvkLookup', 'Gegevens ophalen')}
                    </Button>
                  </div>
                  {kvkData && (
                    <div className="border border-green-200 bg-green-50 rounded-lg p-4 mb-4 text-sm">
                      <p className="font-semibold text-gray-900">{kvkData.bedrijfsnaam}</p>
                      <p className="text-gray-700">
                        {[kvkData.adres, kvkData.postcode, kvkData.plaats].filter(Boolean).join(', ')}
                      </p>
                      <p className="text-gray-500 mt-1">KvK: {kvkNummer}</p>
                    </div>
                  )}
                  <Button onClick={confirmKvk} disabled={!kvkData} loading={busy} className="w-full">
                    {t('onboarding.kvkConfirm', 'Dit is mijn organisatie — verder')}
                  </Button>
                </div>
              </Card>
            )}

            {step === 'contract' && (
              <Card>
                <div className="p-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('onboarding.contractTitle', 'Stap 2: De digitale handdruk')}
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">
                    {t('onboarding.contractText', 'Vul uw mobiele nummer in en onderteken de platformovereenkomst met de 6-cijferige SMS-code. Daarmee is uw organisatie direct ingedekt conform de Wet DBA.')}
                  </p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-72 overflow-y-auto mb-6">
                    <pre className="whitespace-pre-wrap text-xs text-gray-800 font-sans">{contractText || '...'}</pre>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('onboarding.phoneLabel', 'Mobiel nummer tekenbevoegde')}
                      </label>
                      <div className="flex gap-3">
                        <input
                          type="tel"
                          inputMode="tel"
                          value={telefoon}
                          onChange={(e) => setTelefoon(e.target.value)}
                          placeholder="06 12345678"
                          disabled={busy || smsSending}
                          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                        <Button onClick={requestSmsCode} loading={smsSending} variant={smsSent ? 'outline' : undefined} disabled={busy}>
                          {smsSent
                            ? t('onboarding.smsResend', 'Nieuwe code')
                            : t('onboarding.smsRequest', 'Verstuur SMS-code')}
                        </Button>
                      </div>
                    </div>
                    {smsSent && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('onboarding.smsCodeLabel', '6-cijferige SMS-code')}
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={smsCode}
                          onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
                          placeholder="123456"
                          disabled={busy}
                          className="w-40 border border-gray-300 rounded-md px-3 py-2 text-sm tracking-[0.3em] text-center focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    )}
                  </div>
                  <label className="flex items-start mt-4 mb-6">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                      disabled={busy}
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      {t('onboarding.contractAgree', 'Ik ben bevoegd namens de organisatie en ga akkoord met de platformovereenkomst.')}
                    </span>
                  </label>
                  <Button onClick={signContract} loading={busy} disabled={!smsSent || smsCode.length !== 6 || !agreedToTerms} className="w-full">
                    {t('onboarding.contractSign', 'Overeenkomst ondertekenen')}
                  </Button>
                </div>
              </Card>
            )}

            {step === 'profile' && (
              <Card>
                <div className="p-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('onboarding.profileTitle', 'Stap 3: Bedrijfsprofiel inrichten')}
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">
                    {t('onboarding.profileText', 'Upload uw logo en schrijf een korte tekst over uw organisatie. Dit is uw visitekaartje voor de professionals in de marktplaats.')}
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('onboarding.profileLogo', 'Bedrijfslogo')}
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                        className="block w-full text-sm text-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('onboarding.profileContact', 'Contactpersoon')}
                      </label>
                      <input
                        type="text"
                        value={contactpersoon}
                        onChange={(e) => setContactpersoon(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('onboarding.profileDescription', 'Over uw organisatie & bedrijfscultuur')}
                      </label>
                      <textarea
                        rows={5}
                        maxLength={1000}
                        value={beschrijving}
                        onChange={(e) => setBeschrijving(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        placeholder={t('onboarding.profilePlaceholder', 'Wie bent u, hoe werkt u en wat mogen professionals verwachten?')}
                      />
                    </div>
                  </div>
                  {/* Eis 1 gerichte arbo-vrijstelling (migratie 175) — bewust niet blokkerend */}
                  <label className="mt-6 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={arboBeleid}
                      onChange={(e) => setArboBeleid(e.target.checked)}
                      className="mt-1"
                    />
                    <span>
                      <strong className="block text-gray-900">{t('onboarding.arboBeleidTitel', 'Arbobeleidsverklaring (gerichte arbo-vrijstelling)')}</strong>
                      {t('onboarding.arboBeleidTekst', 'Wij verklaren dat de inzet van BeLagom-professionals voortvloeit uit ons arbo- of preventiebeleid en gericht is op het voorkomen van verzuim, het verminderen van psychosociale arbeidsbelasting of re-integratie. Medewerkers betalen geen eigen bijdrage.')}
                      <span className="mt-1 block text-xs text-gray-600">
                        {t('onboarding.arboBeleidGevolg', 'Zonder deze verklaring factureren wij alle uren onder de WKR-vrije ruimte. U kunt dit later aanpassen onder Facturen.')}
                      </span>
                    </span>
                  </label>
                  <Button onClick={saveProfileStep} loading={busy} className="w-full mt-6">
                    {t('onboarding.profileSave', 'Profiel opslaan — verder')}
                  </Button>
                </div>
              </Card>
            )}

            {step === 'stripe' && (
              <Card>
                <div className="p-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('onboarding.paymentTitle', 'Stap 4: B2B-betaalmethode')}
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('onboarding.paymentText', 'U betaalt € 0,- aan vaste abonnementskosten. U rekent later pas af voor daadwerkelijk gewerkte uren via de maandelijkse verzamelfactuur.')}
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800 mb-6">
                    {t('onboarding.paymentNote', 'Facturen betaalt u per factuur online via iDEAL of creditcard. Incasso en overschrijving zijn voor facturen niet mogelijk; een SEPA-machtiging vragen we alleen bij het abonnement op de Crisisdienst.')}
                  </div>
                  <Button onClick={activateCockpit} loading={busy} className="w-full">
                    {t('onboarding.openCockpit', 'HR-Cockpit openen')}
                  </Button>
                </div>
              </Card>
            )}
          </>
        )}

        {/* Succes-venster na ondertekening (Stap 3 spec) */}
        {showSignedModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-8 text-center">
              <div className="text-4xl mb-3">🎉</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {t('onboarding.signedTitle', 'De overeenkomst is getekend. Welkom bij BeLagom!')}
              </h2>
              <p className="text-sm text-gray-600 mb-5">
                {t('onboarding.signedText', 'De platformovereenkomst is succesvol ondertekend. Uw organisatie voldoet vanaf nu direct aan de Wet DBA en de AVG. Doorloop nu de laatste 2 korte stappen om uw HR-Cockpit te openen.')}
              </p>
              <div className="text-left text-sm text-gray-700 space-y-3 mb-6">
                <p>
                  <strong>➡️ {t('onboarding.signedStep1', 'Stap 1: vul uw bedrijfsprofiel aan')}</strong><br />
                  {t('onboarding.signedStep1Text', 'Upload uw logo en schrijf een korte tekst over uw organisatie — uw visitekaartje voor de professionals.')}
                </p>
                <p>
                  <strong>➡️ {t('onboarding.signedStep2', 'Stap 2: koppel de B2B-betaalmethode')}</strong><br />
                  {t('onboarding.signedStep2Text', 'Veilige activatie zonder vaste abonnementskosten. U rekent pas af voor daadwerkelijk gewerkte uren.')}
                </p>
              </div>
              <Button onClick={() => setShowSignedModal(false)} className="w-full">
                {t('onboarding.signedCta', '➔ Start direct met het bedrijfsprofiel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployerOnboardingPage;
